import React, { useState, useEffect, useContext } from 'react';
import AppContext from '../../../contexts/AppContext';
import { hasRole, SUPERADMIN_MANAGERS } from '../../../utils/roleUtils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { MultiSelect } from '../../../components/ui/multi-select';
import { fetchWithAuth } from '../../../utils/fetchWithAuth';
import { Loader2, RefreshCw, Eye, EyeOff, Copy } from 'lucide-react';
import { getAllPermissions, getDefaultPermissions } from '../../../constants/permissions';
import { generateRandomPassword } from '../../../utils/passwordUtils';

export const UserEditDialog = ({ open, user, onClose, onUserUpdated }) => {
  const { currentUser } = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [serviceDesks, setServiceDesks] = useState([]);
  const [selectedServiceDesks, setSelectedServiceDesks] = useState([]);
  const [selectedAllowedCreationDesks, setSelectedAllowedCreationDesks] = useState([]);

  const isSuperAdmin = hasRole(currentUser?.role, 'superadmin');
  const isSuperAdminManager = isSuperAdmin && SUPERADMIN_MANAGERS.includes(currentUser?.email);
  const isTargetSuperAdmin = hasRole(user?.role, 'superadmin');
  const isEditingSelf = user?.id === currentUser?.id;

  const canEditRole = !isEditingSelf
    && (isTargetSuperAdmin ? isSuperAdminManager : isSuperAdmin || !hasRole(user?.role, 'admin+'));

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'user',
    mode: 'rw',
    serviceDeskId: '',
    sectors: '',
    permissions: [],
    allowedTicketCreationDesks: []
  });

  // Filter permission options to only show those not included in role defaults
  const defaultPermissionsForRole = getDefaultPermissions(formData.role);
  const availablePermissionOptions = getAllPermissions().filter(
    option => !defaultPermissionsForRole.includes(option.value)
  );

  // Fetch service desks for superadmin
  useEffect(() => {
    if (open && isSuperAdmin) {
      fetchServiceDesks();
    }
  }, [open, isSuperAdmin]);

  const fetchServiceDesks = async () => {
    try {
      const response = await fetchWithAuth('/service-desks/all');
      const json = await response.json();
      if (json.success) {
        setServiceDesks(json.data);
      }
    } catch (err) {
      console.error('Error fetching service desks:', err);
    }
  };

  useEffect(() => {
    if (user) {
      const serviceDeskIdStr = Array.isArray(user.serviceDeskId)
        ? user.serviceDeskId.join(', ')
        : String(user.serviceDeskId || '');

      const allowedCreationDesks = user.allowed_ticket_creation_desks || [];

      setFormData({
        username: user.username || '',
        email: user.email || '',
        password: '', // Don't pre-fill password
        role: user.role || 'user',
        mode: user.mode || 'rw',
        serviceDeskId: serviceDeskIdStr,
        sectors: Array.isArray(user.sectors) ? user.sectors.join(', ') : '',
        permissions: Array.isArray(user.extraPermissions) ? user.extraPermissions : [],
        allowedTicketCreationDesks: allowedCreationDesks
      });

      // Initialize service desk selections for superadmin
      if (isSuperAdmin) {
        if (user.serviceDeskId === '*') {
          setSelectedServiceDesks(['*']);
        } else if (Array.isArray(user.serviceDeskId)) {
          setSelectedServiceDesks(user.serviceDeskId);
        } else if (user.serviceDeskId) {
          setSelectedServiceDesks([user.serviceDeskId]);
        } else {
          setSelectedServiceDesks([]);
        }

        // Initialize allowed creation desks for technicians
        setSelectedAllowedCreationDesks(allowedCreationDesks);
      }
    }
  }, [user, isSuperAdmin]);

  // Convert service desks to MultiSelect options format
  const serviceDeskOptions = serviceDesks.map(sd => ({
    value: sd.id,
    label: sd.name
  }));

  // Handle multi-select change
  const handleServiceDesksChange = (selected) => {
    setSelectedServiceDesks(selected);

    // Update formData.serviceDeskId
    if (selected.includes('*')) {
      setFormData(prev => ({ ...prev, serviceDeskId: '*' }));
    } else if (selected.length > 0) {
      setFormData(prev => ({ ...prev, serviceDeskId: selected.join(', ') }));
    } else {
      setFormData(prev => ({ ...prev, serviceDeskId: '' }));
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);

    // Reset extra permissions and allowed creation desks when role changes
    if (field === 'role') {
      setFormData(prev => ({ ...prev, permissions: [], allowedTicketCreationDesks: [] }));
      setSelectedAllowedCreationDesks([]);
    }
  };

  const handlePermissionsChange = (selected) => {
    setFormData(prev => ({ ...prev, permissions: selected }));
  };

  const handleAllowedCreationDesksChange = (selected) => {
    setSelectedAllowedCreationDesks(selected);
    setFormData(prev => ({ ...prev, allowedTicketCreationDesks: selected }));
  };

  const handleGeneratePassword = () => {
    const password = generateRandomPassword();
    handleChange('password', password);
    setShowPassword(true);
  };

  const copyPasswordToClipboard = async () => {
    if (formData.password) {
      try {
        await navigator.clipboard.writeText(formData.password);
      } catch (err) {
        console.error('Failed to copy password:', err);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Parse sectors (comma-separated string to array)
      const sectorsArray = formData.sectors
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);

      // Parse serviceDeskId - always as array: [3], [3, 205], or ["*"]
      let serviceDeskId;
      const rawServiceDeskId = formData.serviceDeskId.trim();
      if (rawServiceDeskId === '*') {
        serviceDeskId = ['*'];
      } else if (rawServiceDeskId.includes(',')) {
        serviceDeskId = rawServiceDeskId.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
      } else {
        const parsed = parseInt(rawServiceDeskId);
        if (isNaN(parsed)) {
          throw new Error('Service Desk ID invalide');
        }
        serviceDeskId = [parsed];
      }

      const payload = {
        username: formData.username,
        email: formData.email,
        role: formData.role,
        mode: formData.mode,
        serviceDeskId,
        sectors: sectorsArray,
        permissions: formData.permissions,
        allowedTicketCreationDesks: formData.role === 'technician' ? formData.allowedTicketCreationDesks : []
      };

      // Only include password if it's been set
      if (formData.password) {
        payload.password = formData.password;
      }

      const response = await fetchWithAuth(`/users/${user.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const json = await response.json();

      if (!response.ok) {
        throw new Error(json.error || 'Erreur lors de la mise à jour de l\'utilisateur');
      }

      if (json.success) {
        onUserUpdated();
      } else {
        throw new Error(json.error || 'Erreur lors de la mise à jour de l\'utilisateur');
      }
    } catch (err) {
      console.error('Error updating user:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Modifier l'utilisateur</DialogTitle>
          <DialogDescription>
            Modifiez les informations de {user.username}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="space-y-4 overflow-y-auto flex-1 pr-2">
            <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="edit-username">Nom d'utilisateur *</Label>
              <Input
                id="edit-username"
                value={formData.username}
                onChange={(e) => handleChange('username', e.target.value)}
                autoComplete="off"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-email">Email *</Label>
              <Input
                id="edit-email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                autoComplete="off"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-password">Nouveau mot de passe</Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  id="edit-password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  placeholder="Laissez vide pour ne pas changer"
                  autoComplete="off"
                  className="pr-10"
                />
                {formData.password && (
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                )}
              </div>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={handleGeneratePassword}
                title="Générer un mot de passe"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
              {formData.password && (
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={copyPasswordToClipboard}
                  title="Copier le mot de passe"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Laissez vide pour ne pas changer, ou générez un nouveau mot de passe sécurisé
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {canEditRole && (
            <div className="space-y-2">
              <Label htmlFor="edit-role">Rôle *</Label>
              <Select value={formData.role} onValueChange={(value) => handleChange('role', value)}>
                <SelectTrigger id="edit-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">Utilisateur</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="technician">Technicien</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="superadmin">Super Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="edit-mode">Mode *</Label>
              <Select value={formData.mode} onValueChange={(value) => handleChange('mode', value)}>
                <SelectTrigger id="edit-mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="rw">Lecture/Écriture</SelectItem>
                  <SelectItem value="r">Lecture seule</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Service Desk *</Label>
            {isSuperAdmin ? (
              <>
                <MultiSelect
                  options={serviceDeskOptions}
                  selected={selectedServiceDesks}
                  onChange={handleServiceDesksChange}
                  placeholder="Sélectionner les Service Desks..."
                  allOption={{ value: '*', label: 'Tous les Service Desks' }}
                />
                <p className="text-xs text-muted-foreground">
                  Sélectionnez un ou plusieurs Service Desks, ou "Tous" pour donner accès à tous
                </p>
              </>
            ) : (
              <>
                <Input
                  id="edit-serviceDeskId"
                  value={formData.serviceDeskId}
                  disabled
                  className="bg-gray-100"
                />
                <p className="text-xs text-muted-foreground">
                  Le Service Desk ne peut pas être modifié par un admin
                </p>
              </>
            )}
          </div>

          {formData.role === 'technician' && isSuperAdmin && (
            <div className="space-y-2">
              <Label>Service Desks autorisés pour la création de tickets</Label>
              <MultiSelect
                options={serviceDeskOptions}
                selected={selectedAllowedCreationDesks}
                onChange={handleAllowedCreationDesksChange}
                placeholder="Sélectionner les Service Desks..."
              />
              <p className="text-xs text-muted-foreground">
                Sélectionnez les Service Desks sur lesquels ce technicien peut créer des tickets
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="edit-sectors">Secteurs</Label>
            <Textarea
              id="edit-sectors"
              value={formData.sectors}
              onChange={(e) => handleChange('sectors', e.target.value)}
              placeholder="Ex: Secteur A, Secteur B, Secteur C"
              rows={3}
            />
            <p className="text-xs text-muted-foreground">
              Entrez les secteurs séparés par des virgules
            </p>
          </div>

          <div className="space-y-2">
            <Label>Permissions supplémentaires</Label>
            {availablePermissionOptions.length > 0 ? (
              <MultiSelect
                options={availablePermissionOptions}
                selected={formData.permissions}
                onChange={handlePermissionsChange}
                placeholder="Sélectionner des permissions supplémentaires..."
              />
            ) : (
              <p className="text-sm text-muted-foreground italic">
                Ce rôle possède déjà toutes les permissions disponibles.
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              Permissions par défaut du rôle "{formData.role}" : {defaultPermissionsForRole.length > 0
                ? defaultPermissionsForRole.join(', ')
                : 'aucune'}
            </p>
          </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}
          </div>

          <DialogFooter className="flex-shrink-0 pt-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Annuler
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Enregistrer les modifications
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
